import React, { useState, useReducer, useEffect, useCallback } from 'react';
// ⚠️ Your specified imports
import apiCall from 'shared/services/ApiAdapter';
import { getCurrentUserId } from 'shared/utils/SessionDataUtils';

// Note: Assuming ChartWrapper, LoadingPlaceholder support dynamic classes now
import { ChartWrapper, LoadingPlaceholder, SimpleStackedBarChart, useChartData, MasteryDoughnutChart } from 'modules/dashboard/ChartComponents';
import useStudentEnrollmentData from 'shared/hooks/useStudentEnrollmentData';
import { useMasterList } from 'shared/hooks/useMasterList';
import { Bar, Doughnut } from 'react-chartjs-2';
import { StudentTrendLineChart, useChapterTrendChartAggregator, useQuestionDistributionAggregator, useStudentAssignmentStatusAggregator } from 'modules/dashboard/ChartDataAggregator';

// --- END: Renamed function to Custom Hook ---

// --- CONDITIONAL CHART.JS CONFIGURATION ---

// --- CHART.JS THEME CONFIGURATIONS ---

// Use default/original light options, setting explicit colors for better control
const LIGHT_THEME_CHART_OPTIONS = {
    // General font color for the chart (used by default for tooltips, etc.)
    color: 'rgba(0, 0, 0, 0.9)',

    scales: {
        x: {
            // Light, subtle grid and border lines
            grid: { color: 'rgba(0, 0, 0, 0.1)', borderColor: 'rgba(0, 0, 0, 0.3)' },
            // Dark ticks for high contrast against light background
            ticks: { color: 'rgba(0, 0, 0, 0.8)' },
            title: { color: 'rgba(0, 0, 0, 0.8)' },
        },
        y: {
            grid: { color: 'rgba(0, 0, 0, 0.1)', borderColor: 'rgba(0, 0, 0, 0.3)' },
            ticks: { color: 'rgba(0, 0, 0, 0.8)' },
            title: { color: 'rgba(0, 0, 0, 0.8)' },
        },
    },

    plugins: {
        legend: {
            // Dark legend labels for contrast
            labels: { color: 'rgba(0, 0, 0, 0.9)' }
        },
        title: {
            // Dark title text
            color: 'rgba(0, 0, 0, 1)'
        },
        tooltip: {
            // Optional: Standard dark tooltip background
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#fff',
            bodyColor: '#fff',
        }
    }
};

const DARK_THEME_CHART_OPTIONS = {
    color: 'rgba(255, 255, 255, 0.9)',
    scales: {
        x: {
            // Light, subtle grid and border lines
            grid: { color: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 255, 255, 0.3)' },
            // Light ticks for high contrast against dark background
            ticks: { color: 'rgba(255, 255, 255, 0.8)' },
            title: { color: 'rgba(255, 255, 255, 0.8)' }
        },
        y: {
            grid: { color: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 255, 255, 0.3)' },
            ticks: { color: 'rgba(255, 255, 255, 0.8)' },
            title: { color: 'rgba(255, 255, 255, 0.8)' }
        },
    },
    plugins: {
        legend: { labels: { color: 'rgba(255, 255, 255, 0.9)' } },
        title: { color: 'rgba(255, 255, 255, 1)' },
        tooltip: {
            // Optional: Standard dark tooltip background
            backgroundColor: 'rgba(31, 41, 55, 0.9)',
            titleColor: '#fff',
            bodyColor: '#fff',
        }
    }
};

// --- CHART CONTAINER LOGIC (Updated to pass theme and options) ---

const StudentTrendChartContainer = ({ students, assignmentMetrics, isDarkTheme }) => {
    const getChartData = () => assignmentMetrics;
    const { data, loading } = useChartData(getChartData, [assignmentMetrics, students]);
    const chartOptions = isDarkTheme ? DARK_THEME_CHART_OPTIONS : LIGHT_THEME_CHART_OPTIONS;

    return loading ? (
        <LoadingPlaceholder isDarkTheme={isDarkTheme} />
    ) : (
        <StudentTrendLineChart data={data} students={students} options={chartOptions} />
    );
};

export const ChapterAssignemntChartContainer = ({ chapters, batches, assignmentMetrics, isDarkTheme }) => {

    const { aggregateChapterTrends, chapterCodeToNameMap, batchIdToNameMap } = useChapterTrendChartAggregator(chapters, batches, assignmentMetrics);
    const chartOptions = isDarkTheme ? DARK_THEME_CHART_OPTIONS : LIGHT_THEME_CHART_OPTIONS;

    const { data: chartData, loading } = useChartData(aggregateChapterTrends, [assignmentMetrics, chapterCodeToNameMap, batchIdToNameMap]);
    const hasData = chartData?.labels && chartData.labels.length > 0;

    return loading ? (
        <LoadingPlaceholder isDarkTheme={isDarkTheme} />
    ) : hasData ? (
        <SimpleStackedBarChart
            data={chartData}
            title="Assignments Completed Per Chapter (Grouped by Batch)"
            options={chartOptions}
        />
    ) : (
        <div className={`p-4 text-center ${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}>No completed assignments found or chapter codes specified for analysis.</div>
    );
};


export const QuestionDistributionContainer = ({ assignmentMetrics, isDarkTheme }) => {
    const aggregateMastery = useQuestionDistributionAggregator(assignmentMetrics);
    const chartOptions = isDarkTheme ? DARK_THEME_CHART_OPTIONS : LIGHT_THEME_CHART_OPTIONS;

    const { data, loading } = useChartData(aggregateMastery, [assignmentMetrics]);

    const hasData = data && data.length > 0;

    return loading ? (
        <LoadingPlaceholder isDarkTheme={isDarkTheme} />
    ) : hasData ? (
        <MasteryDoughnutChart
            data={data}
            title="Questions Distribution by Difficulty"
            options={chartOptions}
        />
    ) : (
        <div className={`p-4 text-center ${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}>No question difficulty data available for mastery analysis.</div>
    );
};


export const BatchComparisonChartContainer = ({ students, assignmentMetrics, isDarkTheme }) => {
    const { aggregateStudentStatus, studentIdToNameMap } = useStudentAssignmentStatusAggregator(students, assignmentMetrics);
    const chartOptions = isDarkTheme ? DARK_THEME_CHART_OPTIONS : LIGHT_THEME_CHART_OPTIONS;

    const { data: chartData, loading } = useChartData(aggregateStudentStatus, [assignmentMetrics, studentIdToNameMap]);
    const hasData = chartData?.labels && chartData.labels.length > 0;

    return loading ? (
        <LoadingPlaceholder isDarkTheme={isDarkTheme} />
    ) : hasData ? (
        <SimpleStackedBarChart
            data={chartData}
            title="Assignment Status Breakdown by Student"
            options={chartOptions}
        />
    ) : (
        <div className={`p-4 text-center ${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}>No assignment data available to break down by status.</div>
    );
};


// --- CONFIGURATION & REDUCER ---

const useMockData = false;
const startLoading = () => { };
const stopLoading = () => { };
const displayToast = () => { };


// --- MAIN DASHBOARD COMPONENT ---

// NOTE: I'm adding a dummy prop `isDarkTheme` to simulate theme context.
const AnalyticsDashboard = ({ isDarkTheme = false }) => {
    const userId = getCurrentUserId();
    const enroll = false;
    const [initialLoading, setInitialLoading] = useState(true);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);

    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [selectedChapter, setSelectedChapter] = useState('All');
    const [selectedBatchId, setSelectedBatchId] = useState('All');
    const [selectedStudent, setSelectedStudent] = useState('All');

    const [courses, setCourses] = useState([]);
    const [batches, setBatches] = useState([]);
    const [students, setStudents] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [assignmentMetrics, setAssignmentMetrics] = useState([]);

    const {
        masterData,
        // masterDataLoading, // unused
        // masterDataError // unused
    } = useMasterList({ displayToast, startLoading, stopLoading });

    const {
        students: studentList,
        // enrollments, // unused
        // course: fetchedCourse, // unused
        batches: fetchedBatches,
        // tutors, // unused
        loading: enrollmentLoading,
        // error: enrollmentError, // unused
    } = useStudentEnrollmentData({ courseId: selectedCourseId || null });

    const fetchAssignments = useCallback(async () => {
        if (!selectedCourseId || !fetchedBatches || fetchedBatches.length === 0) {
            setAssignments([]);
            return;
        }

        setAnalyticsLoading(true);
        try {
            const batchIds = fetchedBatches.map(batch => batch.id);
            const batchFilter = Array.from(batchIds).join(',');

            const data = await apiCall(
                '/assignments?batchId.in=' + String(batchFilter) + '&isActive.equals=true',
                false,
                "GET",
                null,
                startLoading,
                stopLoading,
                () => { }
            );
            const list = Array.isArray(data) ? data : data?.content ?? [];
            setAssignments(list);
        } catch (err) {
            console.error("Error fetching assignments:", err);
            setAssignments([]);
        } finally {
            setAnalyticsLoading(false);
        }
    }, [selectedCourseId, fetchedBatches]);

    useEffect(() => {
        fetchAssignments();
    }, [fetchAssignments]);


    // 2. EFFECT: Fetch Courses (Runs ONCE on Mount)
    useEffect(() => {
        const fetchCourses = async () => {
            if (!userId) {
                setInitialLoading(false);
                console.warn("User ID not available, cannot fetch user-specific courses.");
                return;
            }

            setInitialLoading(true);
            try {
                var url = '/courses?isActive.equals=true&createdBy.equals=' + userId;

                const data = await apiCall(
                    url,
                    useMockData,
                    "GET",
                    null,
                    startLoading,
                    stopLoading,
                    displayToast
                );

                const courses = data || [];
                setCourses(courses);

                if (courses.length > 0) {
                    setSelectedCourseId(courses[0].id);
                    setSelectedBatchId('All');
                    setSelectedStudent('All');
                }
            } catch (error) {
                console.error("Failed to fetch courses:", error);
            } finally {
                setInitialLoading(false);
            }
        };

        fetchCourses();
    }, [userId]);

    // 3. EFFECT: Fetch Course Enrollment Data (Placeholder for hook execution)
    useEffect(() => {
        if (!selectedCourseId) return;
    }, [selectedCourseId]);


    // 3.5. EFFECT: Update Local Batches and Students State
    useEffect(() => {
        if (!enrollmentLoading && fetchedBatches && studentList) {
            setBatches(fetchedBatches);
            setStudents(studentList);
            setSelectedStudent('All'); // Reset student filter
        }
    }, [enrollmentLoading, fetchedBatches, studentList, selectedCourseId]);

    // --- 6a. Filtering Logic (Assignments) ---
    useEffect(() => {
        const fildata = (assignments || [])?.filter(a =>
            (selectedBatchId === 'All' || a.batch?.id == selectedBatchId)
            && (selectedStudent === 'All' || a.student?.id == selectedStudent)
        );
        console.log(fildata.length + " " + selectedBatchId + " " + selectedStudent)
        setAssignmentMetrics(fildata);
    }, [assignments, selectedBatchId, selectedStudent]);

    function selectCourse(val) {
        setSelectedCourseId(val);
        setSelectedBatchId('All');
        setSelectedStudent('All');
    }

    const isReady = !initialLoading && !enrollmentLoading && !analyticsLoading;


    // --- Loading and Error Displays (Dynamic Classes) ---
    if (!isReady) {
        let loadingMessage;
        if (initialLoading) { loadingMessage = 'Fetching course list...'; }
        else if (enrollmentLoading) { loadingMessage = 'Fetching students and batches...'; }
        else if (analyticsLoading) { loadingMessage = 'Fetching assignment and metrics data...'; }

        return (
            <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
                <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
                    <LoadingPlaceholder isDarkTheme={isDarkTheme} />
                    <p className="mt-4 text-gray-700 dark:text-gray-300 font-semibold">{loadingMessage}</p>
                </div>
            </div>
        );
    }

    if (!selectedCourseId) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
                <p className="text-gray-700 dark:text-gray-300 text-lg font-semibold">No active courses found for analysis.</p>
            </div>
        );
    }

    // --- Rendered Dashboard (Dynamic Classes) ---
    return (
        <div className="min-h-screen p-2 sm:p-6 md:p-2 bg-white dark:bg-gray-900">
            <header className="flex justify-between items-center mb-4 lg:mb-2 lg:flex-none">
                <h2 className="text-2xl font-extrabold flex-none lg:mr-8 text-gray-900 dark:text-white">
                    <span className="mr-2">🚀</span> Teacher dashboard
                </h2>
            </header>

            {/* Filter Section (Dynamic Classes) */}
            <section className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-lg mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 border-t-4 border-blue-500">

                {/* 1. Course Filter */}
                <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">1. Course</label>
                    <select
                        value={selectedCourseId || ''}
                        onChange={(e) => selectCourse(e.target.value)}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:bg-gray-700 dark:text-white"
                        disabled={initialLoading}
                    >
                        {courses?.map(c => (
                            <option key={c.id} value={c.id}>
                                {c.courseName || c.name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* 2. Batch Filter */}
                <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">2. Batch</label>
                    <select
                        value={selectedBatchId}
                        onChange={(e) => setSelectedBatchId(e.target.value)}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-purple-500 focus:border-purple-500 text-gray-900 dark:bg-gray-700 dark:text-white"
                        disabled={!batches || batches.length === 0 || enrollmentLoading}
                    >
                        <option value="All">All Batches</option>
                        {batches?.map(b => (
                            <option key={b.id} value={b.id}>
                                {b.batchName}
                            </option>
                        ))}
                    </select>
                </div>

                {/* 3. Student Filter */}
                <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">3. Student</label>
                    <select
                        value={selectedStudent}
                        onChange={(e) => setSelectedStudent(e.target.value)}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 dark:bg-gray-700 dark:text-white"
                        disabled={!students || students.length === 0 || enrollmentLoading}
                    >
                        <option value="All">All Students</option>
                        {students?.map(s => (
                            <option key={s.id} value={s.id}>
                                {s.name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Placeholder for Chapter Filter */}
                <div className="hidden lg:block"></div>

            </section>

            {/* --- Charts Section (Dynamic Classes) --- */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Student Trend */}
                <ChartWrapper title="Assessment vs. Student Trend" description="Performance trend (Score %) of each student across assessments." isDarkTheme={isDarkTheme}>
                    {isReady ?
                        <StudentTrendChartContainer students={students} assignmentMetrics={assignmentMetrics} isDarkTheme={isDarkTheme} />
                        : <LoadingPlaceholder isDarkTheme={isDarkTheme} />
                    }
                </ChartWrapper>

                {/* Chapter Trend */}
                <ChartWrapper title="Chapter Assignment Distribution" description="Number of assignments per chapter" isDarkTheme={isDarkTheme}>
                    {isReady ?
                        <ChapterAssignemntChartContainer chapters={masterData.chapters} batches={batches} assignmentMetrics={assignmentMetrics} isDarkTheme={isDarkTheme} />
                        : <LoadingPlaceholder isDarkTheme={isDarkTheme} />
                    }
                </ChartWrapper>

                {/* Mastery */}
                <ChartWrapper title="Assignment Questions Distribution" description="Doughnut chart showing distribution of questions by difficulty level." isDarkTheme={isDarkTheme}>
                    {isReady ?
                        <QuestionDistributionContainer assignmentMetrics={assignmentMetrics} isDarkTheme={isDarkTheme} />
                        : <LoadingPlaceholder isDarkTheme={isDarkTheme} />
                    }
                </ChartWrapper>

                {/* Batch Comparison */}
                <ChartWrapper title="Assignments Student Status" description="Live status showing student assignments current status." isDarkTheme={isDarkTheme}>
                    {isReady ?
                        <BatchComparisonChartContainer students={students} assignmentMetrics={assignmentMetrics} isDarkTheme={isDarkTheme} />
                        : <LoadingPlaceholder isDarkTheme={isDarkTheme} />
                    }
                </ChartWrapper>

            </section>
        </div>
    );
};

export default AnalyticsDashboard;